import React, { Component } from "react";
import { connect } from "react-redux";
import { func, string, number } from "prop-types";
import { TicketApi } from "services/ticket";
import LoadingCube from "sub_components/Common/LoadingCube";
import { TICKET_TYPES, HIGH_LEVEL_TICKET_STATUS } from "utils/TicketConstants";
import { RESOLUTIONS } from "services/config";
import Highcharts from "highcharts/highstock";
import highcharts3d from "highcharts/highcharts-3d";
highcharts3d(Highcharts);

class DonutChart extends Component {
  constructor(props) {
    super(props);

    this.state = {
      tickets: [],
      error: null,
      formattedTicketData: [
        ["Open", 0],
        ["Closed", 1],
      ],
      openTickets: 0,
      closedTickets: 0,
      show: false,
      loading: true,
      total: 0,
      open: 0,
      closed: 0,
    };
  }

  getTickets = () => {
    return new Promise((resolve, reject) => {
      const {
        ticketType,
        company_info: {
          customer: { id },
        },
      } = this.props;

      TicketApi.getTicketCount(ticketType.toLowerCase(), id)
        .then((response) => {
          if (
            response.status === 200 &&
            response?.data &&
            !response.data.error
          ) {
            const open = response?.data?.open;
            const closed = response?.data?.closed;

            this.setState({ open, closed }, () => {
              resolve();
            });
          } else {
            reject(new Error("Failed to fetch ticket counts"));
          }
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        });
    });
  };

  calculateStartAngle = (data) => {
    if (!data) {
      return 0;
    }
    const open = data[0];
    const closed = data[1];
    const amountOpen = open[1];
    const amountClosed = closed[1];
    const totalTickets = amountOpen + amountClosed;
    const angle = (amountOpen / totalTickets) * 360;

    if (amountOpen === 0 && amountClosed === 0) {
      return 0;
    }

    return 90 - angle / 2;
  };
  getGraphSize() {
    const { breakpoint } = this.props;

    let width = 135;
    let height = 130;
    let innerSize = 60;
    let depth = 20;

    if (breakpoint === RESOLUTIONS.MED) {
      width = 173;
      height = 173;
      innerSize = 85;
      depth = 28;
    } else if (breakpoint === RESOLUTIONS.HIGH) {
      width = 253;
      height = 253;
      innerSize = 130;
      depth = 45;
    }

    return { width, height, innerSize, depth };
  }
  getModuleColors = (id) => {
    switch (id) {
      case "account":
        return ["#2a7de1", "#a2a9ad"];
      case "support":
        return ["#a46cf4", "#a2a9ad"];
      case "billing":
        return ["#b5d334", "#a2a9ad"];
      case "storage":
        return ["#00b0b9", "#a2a9ad"];
      case "colocation":
        return ["#00AD5C", "#a2a9ad"];
      default:
        return ["#a46cf4", "#a2a9ad"];
    }
  };

  createDonut = (formattedTicketData) => {
    const { id } = this.props;
    const startAngle = this.calculateStartAngle(formattedTicketData);
    const { width, height, innerSize, depth } = this.getGraphSize();
    const colors = this.getModuleColors(id);

    Highcharts.chart(`donut-container-${id}`, {
      chart: {
        width,
        height,
        type: "pie",

        options3d: {
          enabled: true,
          alpha: 0,
          beta: 57,
        },
      },

      title: {
        text: "",
      },
      tooltip: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          innerSize,
          depth,
          startAngle,
        },

        series: {
          colors,
          edgeColor: "#ffffff33",
          states: {
            inactive: {
              opacity: 1,
            },
          },
          dataLabels: {
            enabled: false,
          },
        },
      },

      series: [
        {
          name: "",
          data: formattedTicketData,
        },
      ],
    });
  };
  componentDidUpdate(prevProps) {
    const { breakpoint, ticketTypePermission } = this.props;

    if (prevProps.breakpoint !== breakpoint) {
      const { openTickets, closedTickets } = this.state;

      this.setState({ error: null, show: true, loading: false }, () => {
        this.createDonut([
          ["Open", openTickets],
          ["Closed", closedTickets],
        ]);
      });
    }

    if (prevProps.ticketTypePermission !== ticketTypePermission) {
      this.getTicketCounts(ticketTypePermission);
    }
  }

  getTicketCounts = async () => {
    const { showTitle, ticketType } = this.props;

    try {
      await this.getTickets();
      const { open, closed } = this.state;

      this.setState(
        {
          error: null,
          show: true,
          loading: false,
          openTickets: open,
          closedTickets: closed,
        },
        () => {
          if (open !== 0 || closed !== 0) {
            this.createDonut([
              ["Open", open],
              ["Closed", closed],
            ]);
          } else {
            if (ticketType.toLowerCase() === "billing") {
              showTitle(false);
            }
            this.createDonut([
              ["Open", 0],
              ["Closed", 1],
            ]);
          }
        }
      );
    } catch (error) {
      console.log(error);
      this.setState({
        error,
        loading: false,
        show: true,
      });
    }
  };

  componentDidMount() {
    const { ticketTypePermission } = this.props;
    this.getTicketCounts(ticketTypePermission);
  }

  render() {
    const { goToTicketHistory, id, ticketType } = this.props;
    const { openTickets, closedTickets, show, loading } = this.state;
    let displayTicketType = ticketType;
    if (ticketType === "Support") {
      displayTicketType = "Any";
    }

    return (
      <>
        {loading && <LoadingCube />}
        <div
          className={
            openTickets === 0 && closedTickets === 0
              ? "donut-chart-container zero-state"
              : `donut-chart-container ${id}`
          }
        >
          {show && (
            <div
              className="donut-title-top"
              onClick={() => goToTicketHistory("Closed", displayTicketType)}
              data-cy="th-closed-link"
            >
              <span className="number numbers7">{closedTickets}</span>
              <span className="text header52">CLOSED</span>
            </div>
          )}
          <div
            id={`donut-container-${id}`}
            onClick={() => goToTicketHistory("Any", ticketType)}
          />
          {show && (
            <div
              className="donut-title-bottom"
              onClick={() => goToTicketHistory("Open", displayTicketType)}
              data-cy="th-open-link"
            >
              <span className="number numbers7">{openTickets}</span>
              <span className="text header52">OPEN</span>
            </div>
          )}
        </div>
      </>
    );
  }
}

DonutChart.propType = {
  goToTicketHistory: func,
  showTitle: func,
  ticketType: string,
  id: string,
  breakpoint: number,
};

DonutChart.defaultProps = {
  goToTicketHistory: () => {},
  showTitle: () => {},
  ticketType: TICKET_TYPES.SUPPORT,
  status: HIGH_LEVEL_TICKET_STATUS.OPEN,
  breakpoint: 1344,
};

function mapStateToProps(state) {
  return {
    auth_status: state.auth_status,
    company_info: state.company_info,
  };
}

export default connect(mapStateToProps, null)(DonutChart);
